import { useCallback, useEffect, useMemo, useState } from "react";
import { listDocCategories } from "@/lib/document/docClient";
import type { DocCategoryNode } from "@/lib/document/categoryTypes";
import {
  flattenDocCategoryTree,
} from "@/lib/document/documentCategoryOptions";
import { listDocAssets } from "@/lib/document/docAssetsClient";
import type { DocAsset } from "@/lib/document/assetTypes";
import {
  getDocDocument,
  getDocVersion,
  listDocDocuments,
  parseBlocksSnapshot,
  parseVariablesSnapshot,
  publishDocVersion,
  restoreDocVersion,
  saveDocVersion,
  upsertDocDocument,
} from "@/lib/document/documentsClient";
import type { DocDocument, DocVersionSummary } from "@/lib/document/documentTypes";
import type { DocVariableSnapshot } from "@/lib/document-builder/versions/snapshotTypes";
import { mergeVariablesFromBlocks } from "@/lib/document-builder/variables/variableFillFields";
import { normalizeDocVariablesForSave } from "@/lib/document-builder/variables/variableEditorHelpers";
import type { DocBlock, DocBlockType } from "@/lib/document-builder/blocks/blockTypes";
import { createDocBlock } from "@/lib/document-builder/blocks/blockDefaults";
import {
  moveDocBlock,
  removeDocBlock,
  sortDocBlocks,
  updateDocBlock,
} from "@/lib/document-builder/blocks/blockReorder";
import { appendBlock } from "./DocumentBlockList";

export type DocEditorSaveFlash = "metadata" | "draft" | "create" | "publish" | "restore";

export function useDocumentDraftEditor() {
  const [categories, setCategories] = useState<Omit<DocCategoryNode, "children">[]>([]);
  const [categoryTree, setCategoryTree] = useState<DocCategoryNode[]>([]);
  const [assets, setAssets] = useState<DocAsset[]>([]);
  const [document, setDocument] = useState<DocDocument | null>(null);
  const [blocks, setBlocks] = useState<DocBlock[]>([createDocBlock("heading", 0)]);
  const [variables, setVariables] = useState<DocVariableSnapshot[]>([]);
  const [versionId, setVersionId] = useState<string | null>(null);
  const [versionNumber, setVersionNumber] = useState<number | null>(null);
  const [versions, setVersions] = useState<DocVersionSummary[]>([]);
  const [currentDraftVersionId, setCurrentDraftVersionId] = useState<string | null>(null);
  const [openDocuments, setOpenDocuments] = useState<DocDocument[]>([]);
  const [compareLeftId, setCompareLeftId] = useState("");
  const [compareRightId, setCompareRightId] = useState("");

  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [changeNotes, setChangeNotes] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [saveFlash, setSaveFlash] = useState<DocEditorSaveFlash | null>(null);

  const dismissSaveFeedback = useCallback(() => {
    setBanner(null);
    setSaveFlash(null);
  }, []);

  const sortedBlocks = useMemo(() => sortDocBlocks(blocks), [blocks]);
  const hasPersistedDraft = currentDraftVersionId != null;
  const canAuthorDraft = document != null;
  const categoryOptions = useMemo(
    () => flattenDocCategoryTree(categoryTree),
    [categoryTree],
  );

  const reloadCategories = useCallback(async () => {
    const r = await listDocCategories();
    if (r.ok) {
      setCategories(r.data.categories);
      setCategoryTree(r.data.tree);
      setCategoryId((prev) => prev || r.data.categories[0]?.id || "");
    }
  }, []);

  const reloadAssets = useCallback(async () => {
    const r = await listDocAssets({ signMedia: true });
    if (r.ok) setAssets(r.data.assets);
  }, []);

  useEffect(() => {
    void reloadCategories();
    void reloadAssets();
    void (async () => {
      const r = await listDocDocuments();
      if (r.ok) setOpenDocuments(r.data.documents);
    })();
  }, [reloadCategories, reloadAssets]);

  async function loadDocument(documentId: string, opts?: { preserveFeedback?: boolean }) {
    setLoading(true);
    setError(null);
    if (!opts?.preserveFeedback) {
      dismissSaveFeedback();
    }
    const docRes = await getDocDocument(documentId);
    if (!docRes.ok) {
      setLoading(false);
      setError(docRes.error);
      return;
    }

    setDocument(docRes.data.document);
    setName(docRes.data.document.name);
    setCategoryId(docRes.data.document.category_id);
    setDescription(docRes.data.document.description ?? "");
    setTags(docRes.data.document.tags ?? []);
    setVersions(docRes.data.versions);
    setCurrentDraftVersionId(docRes.data.current_draft_version_id);

    const compareDefaults = docRes.data.versions;
    if (compareDefaults.length >= 2) {
      setCompareLeftId(compareDefaults[0]?.id ?? "");
      setCompareRightId(compareDefaults[1]?.id ?? "");
    } else {
      setCompareLeftId("");
      setCompareRightId("");
    }

    const draftRes = await getDocVersion({ documentId, draft: true });
    if (draftRes.ok) {
      setVersionId(draftRes.data.version.id);
      setVersionNumber(draftRes.data.version.version_number);
      setBlocks(parseBlocksSnapshot(draftRes.data.version.blocks_snapshot));
      setVariables(parseVariablesSnapshot(draftRes.data.version.variables_snapshot));
    } else {
      setVersionId(null);
      setVersionNumber(null);
      setBlocks([createDocBlock("heading", 0)]);
      setVariables([]);
    }

    setLoading(false);
  }

  async function createDocument() {
    if (!name.trim() || !categoryId) {
      setError("Name and category are required.");
      return;
    }
    setSaving(true);
    setError(null);
    setBanner(null);
    setSaveFlash(null);
    const r = await upsertDocDocument({
      name: name.trim(),
      category_id: categoryId,
      description: description.trim() || null,
      tags,
    });
    setSaving(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setDocument(r.data.document);
    setSaveFlash("create");
    setBanner("Document created. Add blocks and save a draft version.");
    await loadDocument(r.data.document.id, { preserveFeedback: true });
  }

  async function saveMetadata() {
    if (!document) return;
    setSaving(true);
    setError(null);
    dismissSaveFeedback();
    const r = await upsertDocDocument({
      id: document.id,
      name: name.trim(),
      category_id: categoryId,
      description: description.trim() || null,
      tags,
    });
    setSaving(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setDocument(r.data.document);
    setSaveFlash("metadata");
  }

  async function saveDraftVersion() {
    if (!document) {
      setError("Create the document first.");
      return;
    }
    setSaving(true);
    setError(null);
    dismissSaveFeedback();
    const mergedVariables = mergeVariablesFromBlocks(sortedBlocks, variables);
    const normalizedVars = normalizeDocVariablesForSave(mergedVariables);
    if ("error" in normalizedVars) {
      setSaving(false);
      setError(normalizedVars.error);
      return;
    }
    const r = await saveDocVersion({
      document_id: document.id,
      blocks: sortedBlocks,
      variables: normalizedVars,
      change_notes: changeNotes.trim() || null,
    });
    setSaving(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setVersionId(r.data.version.id);
    setVersionNumber(r.data.version.version_number);
    setBlocks(parseBlocksSnapshot(r.data.version.blocks_snapshot));
    setVariables(parseVariablesSnapshot(r.data.version.variables_snapshot));
    setSaveFlash("draft");
    setBanner(
      r.data.created
        ? `Draft version ${r.data.version.version_number} created.`
        : `Draft version ${r.data.version.version_number} saved.`,
    );
    await loadDocument(document.id, { preserveFeedback: true });
  }

  async function publishDraft(versionIdToPublish: string) {
    if (!document) return;
    setSaving(true);
    setError(null);
    const r = await publishDocVersion({
      document_id: document.id,
      version_id: versionIdToPublish,
    });
    setSaving(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setSaveFlash("publish");
    setBanner(`Published version ${r.data.version.version_number}.`);
    await loadDocument(document.id, { preserveFeedback: true });
  }

  async function restoreVersion(sourceVersionId: string) {
    if (!document) return;
    setSaving(true);
    setError(null);
    const r = await restoreDocVersion({
      document_id: document.id,
      version_id: sourceVersionId,
    });
    setSaving(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setSaveFlash("restore");
    setBanner(
      r.data.created
        ? `Restored to new draft v${r.data.version.version_number}.`
        : "Draft updated from version restore.",
    );
    await loadDocument(document.id, { preserveFeedback: true });
  }

  function addBlock(type: DocBlockType) {
    setBlocks((prev) => appendBlock(prev, type));
  }

  function moveBlock(blockId: string, direction: "up" | "down") {
    setBlocks((prev) => moveDocBlock(prev, blockId, direction));
  }

  function removeBlock(blockId: string) {
    setBlocks((prev) => {
      const next = removeDocBlock(prev, blockId);
      return next.length > 0 ? next : [createDocBlock("paragraph", 0)];
    });
  }

  function updateBlock(block: DocBlock) {
    setBlocks((prev) => updateDocBlock(prev, block));
  }

  function resetNewDocument() {
    setDocument(null);
    setVersionId(null);
    setVersionNumber(null);
    setVersions([]);
    setCurrentDraftVersionId(null);
    setCompareLeftId("");
    setCompareRightId("");
    setBlocks([createDocBlock("heading", 0)]);
    setVariables([]);
    setName("");
    setDescription("");
    setTags([]);
    setChangeNotes("");
    dismissSaveFeedback();
    setError(null);
  }

  return {
    categories,
    categoryOptions,
    assets,
    document,
    blocks: sortedBlocks,
    variables,
    setVariables,
    name,
    setName,
    categoryId,
    setCategoryId,
    description,
    setDescription,
    tags,
    setTags,
    changeNotes,
    setChangeNotes,
    versionId,
    versionNumber,
    versions,
    currentDraftVersionId,
    hasPersistedDraft,
    canAuthorDraft,
    openDocuments,
    compareLeftId,
    compareRightId,
    setCompareLeftId,
    setCompareRightId,
    loading,
    saving,
    error,
    banner,
    saveFlash,
    dismissSaveFeedback,
    createDocument,
    saveMetadata,
    saveDraftVersion,
    publishDraft,
    restoreVersion,
    loadDocument,
    addBlock,
    moveBlock,
    removeBlock,
    updateBlock,
    resetNewDocument,
    reloadAssets,
  };
}
